/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventFormFields.tsx
import css from '@emotion/css/macro'
import { useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import {
  formatWithZoneLabel,
  instantInZone,
} from '@sportninja/common/ts-utils/luxon-permanent-dst'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  DAYS_OF_WEEK,
  EVENT_IS_PUBLIC_LABEL,
  EVENT_IS_PUBLIC_NOTE,
  RECURRENCE_TYPES,
} from '@sportninja/common/features/event/constants'
import { EVENT_CATEGORY_OPTIONS } from '@sportninja/common/features/event/eventCategories'
import {
  buildEndsAt,
  EVENT_LENGTH_CUSTOM,
  EVENT_LENGTH_DEFAULT,
  EVENT_LENGTH_OPTIONS,
} from '@sportninja/common/features/event/eventLength'
import type { EventFormValues } from '@sportninja/common/features/event/types'
import { EDIT_SCOPE_OPTIONS } from '@sportninja/common/features/event/seriesScope'
import {
  CountryProvinceField,
  FieldRow,
  NumberField,
  SelectField,
  TextField,
} from 'src/features/profile/player/setupModal/components/FormikField'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import SelectMultiInput from 'src/components/ts-components/SelectMultiInput'
import ScrollContainer from 'src/components/ts-components/ScrollContainer'
import EventDateTimeField from './EventDateTimeField'
import EventTimezoneField from './EventTimezoneField'
import EventLocationFields from './EventLocationFields'
import EventTeamField from './EventTeamField'
import {
  showsAddressFields,
  showsTimezoneField,
} from '@sportninja/common/features/event/venueSelection'
import { useVenues } from '@sportninja/common/features/game/gameForm/hooks/useGameOptions'

/**
 * Zone abbreviation ("PDT", "EST") for an IANA zone, or for the browser's own
 * zone when none is given. Same Luxon `ZZZZ` convention the game list uses.
 */
const zoneAbbr = (timezone?: string, at?: string): string => {
  //  Labelled for the date being AUTHORED, not for today: an abbreviation is a
  //  property of an instant, so a form filled out in August for a November
  //  event would otherwise carry August's label. Falls back to now while no
  //  date has been picked yet.
  const on = at ? DateTime.fromISO(at) : DateTime.local()
  const value = on.isValid ? on : DateTime.local()

  if (!timezone) {
    return value.toFormat('ZZZZ')
  }
  //  Through the permanent-DST helpers so a province that abolished its clock
  //  change gets the offset it kept and the label its residents use, whatever
  //  the viewer's timezone database still carries.
  return formatWithZoneLabel(
    instantInZone(value.toISO() ?? '', timezone),
    timezone,
    'ZZZZ'
  )
}

/**
 * Event Length — a duration the user picks, stored as an end INSTANT.
 *
 * The API takes `ends_at`, not a duration, so this select is an authoring
 * convenience: choosing "1 hour" writes `starts_at + 60min` into `ends_at`.
 * Two behaviours worth knowing:
 *
 * - **Custom** leaves `ends_at` untouched and reveals the end picker, so an
 *   arbitrary duration (45 min, multi-day) stays exactly as authored.
 * - Changing `starts_at` RECOMPUTES `ends_at` for a preset, so moving an event
 *   keeps its length instead of silently stretching it.
 *
 * Every event has a length — there is no "no end time" choice. A legacy event
 * with `ends_at: null` opens at the 1-hour default and GAINS an end on save.
 */
const EventLengthField: React.FC = () => {
  const { values, setFieldValue, setFieldError } =
    useFormikContext<EventFormValues>()
  const length = values.eventLength || EVENT_LENGTH_DEFAULT
  const startsAt = values.starts_at

  // Keep `ends_at` in step when the START moves. Custom is exempt: the user
  // typed that end explicitly, so shifting it would discard their input.
  useEffect(() => {
    if (length === EVENT_LENGTH_CUSTOM) {
      return
    }
    const next = buildEndsAt(startsAt, length)
    if (next !== null && next !== values.ends_at) {
      setFieldValue('ends_at', next)
      if (next) {
        setFieldError('ends_at', undefined)
      }
    }
    // `values.ends_at` is read to avoid a redundant set, not to drive the
    // effect — including it would re-run on the very change this makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startsAt, length])

  return (
    <SelectField
      name='eventLength'
      label={capitalizeWords('Event Length')}
      placeholder={capitalizeWords('Select length')}
      options={EVENT_LENGTH_OPTIONS}
    />
  )
}

/**
 * Notes textarea bound to Formik.
 *
 * Labelled "Description" for the admin. The Formik field is already
 * `description`; the API calls it `details`, and `buildEventPayload` is the
 * single point where the two names meet.
 */
const EventDescriptionField: React.FC = () => {
  const [field, meta, helpers] = useField('description')
  const { setFieldError } = useFormikContext<EventFormValues>()
  const show = Boolean(meta.touched && meta.error)
  return (
    // TextAreaInput returns a fragment (label row + textarea as siblings), so
    // without this wrapper both become children of the form's 16px gap and the
    // label sits twice as far from its field as every other label. The cell
    // restores the 8px the other fields get from FieldCell.
    <div css={fieldCellStyle}>
      <TextAreaInput
        name='description'
        label={capitalizeWords('Description')}
        value={field.value ?? ''}
        setValue={(v: string) => {
          helpers.setValue(v)
          if (meta.error) {
            setFieldError('description', undefined)
          }
        }}
        // TextAreaInput reads fieldErrors as an ARRAY of erroring field names
        // (it calls fieldErrors.includes(name)) — unlike TextInput, which reads
        // it as a record. Passing an object here crashes on `.includes`.
        fieldErrors={show ? ['description'] : []}
        customError={show ? { description: meta.error } : {}}
        placeholder={capitalizeWords('Describe the event')}
        maxLength={2000}
        // autoSize keeps it at three rows but lets it grow as the user types,
        // rather than a fixed box with an inner scrollbar.
        autoSize={{ minRows: 3, maxRows: 8 }}
        showCharacterCount
      />
    </div>
  )
}

/**
 * Public/private toggle bound to Formik's `isPublic`.
 *
 * Rendered for TEAM events only — a competition event's visibility belongs to
 * the competition, so the admin is not asked. `buildEventPayload` gates the
 * key on the same condition (`hasIsPublicField`), so a hidden toggle never
 * sends a value.
 */
const EventIsPublicField: React.FC = () => {
  const [field, , helpers] = useField<boolean>('isPublic')
  return (
    <div css={switchRowStyle}>
      <SwitchInput
        title={capitalizeWords(EVENT_IS_PUBLIC_LABEL)}
        subtitle={EVENT_IS_PUBLIC_NOTE}
        value={Boolean(field.value)}
        onChange={(value) => helpers.setValue(value)}
      />
    </div>
  )
}

/**
 * Multi-select of weekdays bound to Formik's `daysOfWeek` (number[]). Only
 * rendered when a recurrence type is chosen — "no days" is meaningless for a
 * one-off event.
 *
 * `SelectMultiInput` works in string values/options (antd `Select` convention
 * for `mode='multiple'`), so day numbers are stringified at this boundary and
 * parsed back on change; Formik itself keeps storing numbers, matching the
 * `days_of_week: number[]` the API expects.
 */
const DAY_OPTIONS = DAYS_OF_WEEK.map((day) => ({
  label: day.label,
  value: String(day.value),
}))

/** RECURRENCE_TYPES values already match SelectField's expected shape. */
const RECURRENCE_OPTIONS = RECURRENCE_TYPES.map((option) => ({ ...option }))

const DaysOfWeekField: React.FC = () => {
  const [field, meta, helpers] = useField<number[]>('daysOfWeek')
  const { setFieldError } = useFormikContext<EventFormValues>()
  const show = Boolean(meta.touched && meta.error)
  return (
    <SelectMultiInput
      name='daysOfWeek'
      label={capitalizeWords('Days Of Week')}
      placeholder={capitalizeWords('Select days')}
      options={DAY_OPTIONS}
      value={(field.value ?? []).map(String)}
      onChange={(values) => {
        helpers.setValue(values.map(Number))
        if (meta.error) {
          setFieldError('daysOfWeek', undefined)
        }
      }}
      fieldErrors={show ? ['daysOfWeek'] : []}
      customError={show ? { daysOfWeek: meta.error } : {}}
    />
  )
}

interface EventFormFieldsProps {
  /**
   * The competition the event belongs to. Present ONLY for a competition
   * event — it scopes the `/venues` list, so its absence is what hides the
   * Location/Facility pickers on a team-owned event.
   */
  scheduleId?: string
  /**
   * True when editing an event that belongs to a repeating series — reveals
   * the scope select. Never set for create, or for a one-off event.
   */
  isSeries?: boolean
}

/** All fields of the event add/edit form. */
const EventFormFields: React.FC<EventFormFieldsProps> = ({
  scheduleId,
  isSeries = false,
}) => {
  const { values } = useFormikContext<EventFormValues>()
  const isRecurring = values.recurrenceType !== 'none'
  // Editing ONE occurrence cannot change the series' repeat rule, and the API
  // rejects a scope=this save that carries it — so the rule fields are hidden
  // rather than shown as no-ops. Switching to 'following'/'all' brings them
  // back. A one-off event (no scope select) always shows them.
  const canEditRecurrence = !isSeries || values.editScope !== 'this'
  // Gated on `isRecurring` so the day list cannot appear for a one-off event —
  // it is nested inside the recurring block today, but the flag should not
  // depend on the JSX nesting.
  const showDaysOfWeek =
    isRecurring &&
    (values.recurrenceType === 'weekly' || values.recurrenceType === 'biweekly')
  const eventLength = values.eventLength || EVENT_LENGTH_DEFAULT
  // The SELECTED zone drives every date field, so the labels and the UTC
  // conversion follow the Timezone select instead of being frozen at whatever
  // the form was seeded with. `values.timezone` comes from the owning
  // account's zone (see EventModal), falling back to the browser's.
  // One abbreviation for every date field on the form — they all author in the
  // same zone, so computing it per field would repeat the same work.
  const selectedTimezone = values.timezone
  const zone = zoneAbbr(selectedTimezone, values.starts_at)
  // Location/Facility only exist for a competition event — `/venues` is scoped
  // to a schedule, so there is no list to offer without one.
  const showLocationPicker = Boolean(scheduleId)
  // Team is gated on the same signal, and for the same reason:
  // `/schedules/:id/teams/simple` needs a competition to scope to. A
  // team-owned event already knows its team and is never asked.
  const showTeamPicker = Boolean(scheduleId)
  // A picked venue owns the address, so the address block steps aside for it.
  // With no picker at all (a team event) it is simply always shown, as before.
  const showAddressFields = showsAddressFields(
    values.isCustomLocation,
    showLocationPicker
  )
  // Same React Query key as EventLocationFields' own call, so this reads the
  // cached list rather than firing a second request. Idle without a schedule.
  const { venues } = useVenues(scheduleId, true, showLocationPicker)
  const selectedVenue = venues.find((venue) => venue.id === values.venueId)
  // The zone is only settled by a venue that HAS one — otherwise the admin
  // still picks it, including while no location is selected at all.
  const showTimezone = showsTimezoneField(values.venueId, selectedVenue)

  return (
    <ScrollContainer css={scrollAreaStyle}>
      <div css={formStyle}>
        {/* First field on purpose: it decides how much of the series the rest of
            the form applies to, and whether the repeat rule is editable at all. */}
        {isSeries && (
          <FieldRow cols={1}>
            <SelectField
              name='editScope'
              label={capitalizeWords('Apply Changes To')}
              placeholder={capitalizeWords('Select events')}
              options={EDIT_SCOPE_OPTIONS.map((option) => ({
                value: option.scope,
                label: capitalizeWords(option.label),
              }))}
            />
          </FieldRow>
        )}

        {/* Team joins this row for a competition event, making it three
            columns; a team-owned event keeps the original pair. Placed after
            Event Name because it qualifies the event rather than identifies
            it — the same reason it sits below the name on mobile. */}
        <FieldRow cols={showTeamPicker ? 3 : 2} collapseAt={420}>
          {/* Optional — no required marker, and a blank selection is valid. */}
          <SelectField
            name='category'
            label={capitalizeWords('Event Type')}
            placeholder={capitalizeWords('Select type')}
            options={EVENT_CATEGORY_OPTIONS}
            showSearch
          />
          <TextField
            name='name'
            label={capitalizeWords('Event Name')}
            placeholder={capitalizeWords('Enter event name')}
            required
          />
          {showTeamPicker && <EventTeamField scheduleId={scheduleId!} />}
        </FieldRow>

        <EventDescriptionField />

        {/* Directly under Description, and only for a TEAM event — a
            competition event inherits its visibility from the competition. */}
        {!showTeamPicker && <EventIsPublicField />}

        {/* FieldRow defaults to collapsing at 560px of CONTAINER width, but the
            modal is 40vw — already under that on a 1280px screen, so the default
            would stack these two rows almost immediately. 420px keeps them
            side-by-side until the modal is genuinely too narrow. */}
        {/* Start date, start time, timezone and length read as one sentence —
            "starts on this day, at this time, in this zone, for this long" — so
            they share a row. The date/time pair is ONE component that renders its
            own two halves, so this is a 3-slot row whose first slot holds two
            controls; the widths below give the pair twice either aside's share.

            The modal was widened to carry this (see EventModal) — at the old
            width these controls were each too narrow to read. */}
        <FieldRow cols={1}>
          <div css={dateTimePairRowStyle}>
            <div css={dateTimePairMainStyle}>
              <EventDateTimeField
                name='starts_at'
                label='Start'
                accountTimezone={selectedTimezone}
                zoneAbbr={zone}
                boundary='start'
                required
              />
            </div>
            {/* Directly after Start Time, because it QUALIFIES that time — the
                two are read together ("6 PM, Toronto"). It gets the same share as
                Event Length beside it; the IANA names are long, so the select
                truncates with an ellipsis and shows the full value on open. */}
            {showTimezone && (
              <div css={dateTimePairAsideStyle}>
                <EventTimezoneField />
              </div>
            )}
            <div css={dateTimePairAsideStyle}>
              <EventLengthField />
            </div>
          </div>
        </FieldRow>
        {/* The end picker only appears for Custom. Every other choice derives
            `ends_at` from the start, so showing a field the dropdown is about to
            overwrite would be misleading.

            The end date/time keeps the row to itself: Arrival Time now pairs
            with Repeat below instead, so there is no companion to place here. */}
        {eventLength === EVENT_LENGTH_CUSTOM && (
          <FieldRow cols={1}>
            <div css={dateTimePairRowStyle}>
              <div css={dateTimePairMainStyle}>
                <EventDateTimeField
                  name='ends_at'
                  label='End'
                  accountTimezone={selectedTimezone}
                  zoneAbbr={zone}
                  boundary='end'
                />
              </div>
            </div>
          </FieldRow>
        )}

        {/* Arrival Time and Repeat share a row — both are short, single-value
            settings that qualify the event's timing, so they read as a pair.

            When the repeat rule is hidden (editing ONE occurrence of a series)
            Arrival Time takes the full width rather than sitting half-width
            beside an empty gap. */}
        {canEditRecurrence ? (
          <FieldRow cols={2} collapseAt={420}>
            <NumberField
              name='arrivalTimeMinutes'
              label={capitalizeWords('Arrival Time (Minutes Before)')}
              placeholder={capitalizeWords('Enter minutes')}
            />
            <SelectField
              name='recurrenceType'
              label={capitalizeWords('Repeat')}
              placeholder={capitalizeWords('Select repeat')}
              options={RECURRENCE_OPTIONS}
            />
          </FieldRow>
        ) : (
          <FieldRow cols={1}>
            <NumberField
              name='arrivalTimeMinutes'
              label={capitalizeWords('Arrival Time (Minutes Before)')}
              placeholder={capitalizeWords('Enter minutes')}
            />
          </FieldRow>
        )}

        {/* The rule's details, under the Repeat select that turns them on. Gated
            on `canEditRecurrence` too: editing ONE occurrence hides the whole
            rule, and `isRecurring` alone would still show these for a series
            whose repeat select is not on screen. */}
        {canEditRecurrence && isRecurring && (
          <>
            {showDaysOfWeek && (
              <FieldRow cols={1}>
                <DaysOfWeekField />
              </FieldRow>
            )}
            {/* Only the cut-off needs a field — the rule starts at the event's
                own Start Date & Time, so a separate "starts on" would just be a
                second way to say the same thing. Sent verbatim as
                `repeat_until`. */}
            <FieldRow cols={1}>
              <EventDateTimeField
                name='repeatUntil'
                label='Repeat Until'
                accountTimezone={selectedTimezone}
                zoneAbbr={zone}
              />
            </FieldRow>
          </>
        )}

        {/* Mirrors the add/edit Game modal. Picking a location hides everything
            below it; "Use A Custom Location" brings the address block back. */}
        {showLocationPicker && <EventLocationFields scheduleId={scheduleId!} />}

        {showAddressFields && (
          <>
            {/* Location name gets the full width: it is free text and the longest
            value in the block ("North Vancouver Sports Centre"), so pairing it
            with a street would truncate both. */}
            <FieldRow cols={1}>
              <TextField
                name='addressName'
                label={capitalizeWords('Location Name')}
                placeholder={capitalizeWords('Enter location name')}
              />
            </FieldRow>
            {/* The two street lines share a row — they are the same field split in
            two, so they read as one address block. */}
            <FieldRow cols={2} collapseAt={420}>
              <TextField
                name='addressStreet1'
                label={capitalizeWords('Address Line 1')}
                placeholder={capitalizeWords('Street address')}
              />
              <TextField
                name='addressStreet2'
                label={capitalizeWords('Address Line 2')}
                placeholder={capitalizeWords('Apartment, suite, unit')}
              />
            </FieldRow>
            {/* Country/Province ahead of City/Postal Code: province narrows to the
            chosen country, so the wider geography is settled first and the row
            order follows the order the fields are actually filled in. */}
            <CountryProvinceField
              countryName='addressCountryId'
              provinceName='addressProvinceId'
              //  Zeroes the pair's own 8px bottom margin. This form spaces its rows
              //  with the container's 16px gap, so that margin stacked on top of it
              //  and left City/Postal Code sitting 24px below Country/Province while
              //  every other row was 16px apart.
              containerStyle={noBottomMarginStyle}
            />
            <FieldRow cols={2} collapseAt={420}>
              <TextField
                name='addressCity'
                label={capitalizeWords('City')}
                placeholder={capitalizeWords('Enter city')}
              />
              <TextField
                name='addressPostalCode'
                label={capitalizeWords('Postal Code')}
                placeholder={capitalizeWords('Enter postal code')}
              />
            </FieldRow>
          </>
        )}
      </div>
    </ScrollContainer>
  )
}

export default EventFormFields

// ---- styled ----
/* Uncapped, a tall form grows the modal to the window's height and
   BaseModal's body scrolls it instead of ScrollContainer. Capping the scroll
   area keeps the modal's header and footer fixed while the fields scroll
   between them. `vh` units track the viewport rather than the modal,
   which is what the overflow needs to respect.

   The cap lives on `ScrollContainer` (the element that scrolls), not on the
   fields wrapper inside it. No right padding here: ScrollContainer's viewport
   already reserves the scrollbar gutter itself (`padding-right: size + 6`), so
   a second inset would read as dead space along the right edge of every row. */
const scrollAreaStyle = css`
  max-height: min(60vh, 640px);
`
/*
 * `CountryAndProvinceInput` carries a `margin-bottom: 8px` of its own, for
 * forms that lay their fields out with plain stacking. This form uses the
 * container's `gap: 16px` instead, so that margin ADDED to the gap and made
 * the row after Country/Province sit 24px down where every other row is 16px.
 *
 * Overridden here rather than removed from the shared input: ~9 other surfaces
 * (player form, location modal, signup, account settings) rely on that margin.
 */
const noBottomMarginStyle = css`
  margin-bottom: 0;
`

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  container-type: inline-size;
`

/* A date/time pair plus its companion field(s) on one line — used by BOTH the
   start row (date + time + timezone + length) and the Custom end row (date +
   time + arrival), so the two line up column-for-column.

   The date/time pair is a single component rendering its own two halves, so
   the start row has three slots, not four. `2 1 0` vs `1 1 0` gives the pair
   twice either companion's width, which lands the controls at roughly equal
   size.

   `min-width: 0` on both: without it a flex child refuses to shrink below its
   content, and the pickers would push the row wider than the modal. */
const dateTimePairRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  /* Below this the three controls cannot hold a readable width, so the
     companion field drops to its own line beneath the date/time pair. */
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 16px;
  }
`

const dateTimePairMainStyle = css`
  flex: 2 1 0;
  min-width: 0;

  @media (max-width: 900px) {
    width: 100%;
  }
`

const dateTimePairAsideStyle = css`
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: 900px) {
    width: 100%;
  }
`

// SwitchInput renders its text block and the pill switch as siblings meant to
// sit in a flex row — label/note on the left, switch on the right. Matches the
// player form's RosterInfoSection wrapper.
const switchRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;

  .ant-switch {
    align-self: flex-start !important;
    margin-top: 2px;
  }
`

const fieldCellStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`
